/*
 * Account edit hub — /profile/edit (CMT-2026-09-03-026, Duncan spec +
 * extension BOARDROOM-2026-09-03-063546 / 064610).
 *
 * Duncan ruling: NO new design — "the existing Profile-complete card IS the
 * design." This screen is that card (the wizard receipt's summary rows —
 * shared SummaryCard component) promoted to an ACCOUNT HUB:
 *
 *  - The account holder's OWN card, hydrated from the SAVED profile
 *    (profileStore.loadSelfDraft — real Cognito attributes + the device-local
 *    extras seam; never a fabricated value, honest loading/error states).
 *    Every row carries a per-row EDIT PENCIL deep-linking into the wizard
 *    step that OWNS the field (`/get-ready/profile/step-N?hub=1`) — the
 *    wizard opens PRE-FILLED (its hydration hold) and Save returns HERE.
 *  - A card per MANAGED CHILD, read from the SAME store groupings the
 *    Profile screen renders (assignStore — own children / someone else's
 *    children / deferred, the ratified three-way partition of
 *    BOARDROOM-2026-09-02-152856 part C, parity per adversarial review F2:
 *    LINKED deferred ChildRecords keep an edit affordance on Profile, so
 *    they keep a pencil-carrying card here too; only assignment-DERIVED
 *    deferred entries — no ChildRecord, display-only on Profile — have NO
 *    card, honestly: there is no managed profile behind them to edit).
 *    Child pencils deep-link into the EXISTING child wizard
 *    (`/profile/children/{key}/step-N?hub=1`) — no new child editor.
 *  - An INCOMPLETE own profile shows the wizard's own "Still needed …" +
 *    "Add these now" affordance (same derivation: missingList /
 *    firstIncompleteStep) — completion is DERIVED from the data, never a
 *    stored flag.
 *  - Identity lock (PROGRAMME-RULE-IDENTITY-FIELD-EDIT-LOCK-BY-VERIFICATION-
 *    LEVEL-V1): at level <= 2 the identity rows (name / DOB / gender) render
 *    WITHOUT a pencil — no dead affordance into a read-only step. Managed
 *    children are never locked (the level is the ACCOUNT's).
 */
import { useCallback, useEffect, useState, useSyncExternalStore } from 'react'
import type { CSSProperties } from 'react'
import { useNavigate } from 'react-router-dom'
import { useShellChrome } from '../components/Shell'
import { useLocale } from '../i18n/useLocale'
import { loadIdentityVerificationLevel, loadSelfDraft } from './wizard/profileStore'
import { DEFAULT_WIZARD_CONFIG } from './wizard/wizardConfig'
import {
  ageFromDob, allValid, firstIncompleteStep, missingList, suggestedJuniorShirt, summaryRows,
  type GateContext, type SummaryRow, type WizardDraft, EMPTY_DRAFT,
} from './wizard/wizardModel'
import { SummaryCard } from './wizard/SummaryCard'
import {
  getAssignStoreSnapshot, getChildProfile, subscribeAssignStore, type ChildRecord,
} from './orders/assignStore'
import { childRowNote, guestSigningState } from './Profile'

const kickerStyle: CSSProperties = {
  fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '.1em',
  textTransform: 'uppercase', color: 'var(--text-faint)', margin: '0 2px 8px',
}
const cardHeadStyle: CSSProperties = {
  display: 'flex', alignItems: 'baseline', gap: 10, margin: '0 2px 8px',
}
const warnBoxStyle: CSSProperties = {
  display: 'flex', gap: 10, background: 'rgba(230,193,90,.09)',
  border: '1px solid rgba(230,193,90,.3)', borderRadius: 12, padding: '12px 13px',
  margin: '0 0 12px',
}

/** Identity-core rows — pencil-less under the L2 lock (read-only steps). */
const IDENTITY_ROW_IDS = new Set<SummaryRow['id']>(['name', 'dob', 'gender', 'genderIdentity'])

/** Child draft = the saved `child_{key}` record, else the identity facts
 *  already on the ChildRecord — the same seed the child wizard opens with
 *  (never an invention). */
function childDraft(c: ChildRecord): WizardDraft {
  return getChildProfile(c.key) ?? {
    ...EMPTY_DRAFT,
    first: c.first, last: c.last,
    dobD: c.dobD, dobM: c.dobM, dobY: c.dobY,
    gender: c.gender,
  }
}

export function ProfileEditHub() {
  const { showPageHead } = useShellChrome()
  const { t, dir } = useLocale()
  const navigate = useNavigate()
  const backChev = dir === 'rtl' ? '›' : '‹'

  // --- OWN profile: hydrate from the SAVED profile, honest states ---
  const [draft, setDraft] = useState<WizardDraft | null>(null)
  // 'signedout' (review F5) is DISTINCT from 'error': a null loadSelfDraft
  // result means NO SESSION — "check your connection" would be a lie there.
  const [state, setState] = useState<'loading' | 'ready' | 'error' | 'signedout'>('loading')
  const [identityLocked, setIdentityLocked] = useState(false)
  const [attempt, setAttempt] = useState(0)
  useEffect(() => {
    let cancelled = false
    setState('loading')
    Promise.all([
      loadSelfDraft(),
      // Fail-open like every level read (resolves 3 on its own failures).
      loadIdentityVerificationLevel().catch(() => 3 as const),
    ])
      .then(([self, level]) => {
        if (cancelled) return
        // null = no session — App.tsx gates these routes authed, but never
        // assume: render the honest signed-out state, not fabricated blanks
        // and not the connection-failure copy (F5).
        setDraft(self)
        setIdentityLocked(level <= 2)
        setState(self ? 'ready' : 'signedout')
      })
      .catch(() => { if (!cancelled) setState('error') })
    return () => { cancelled = true }
  }, [attempt])

  // --- managed children: the SAME store groupings Profile renders ---
  const store = useSyncExternalStore(subscribeAssignStore, getAssignStoreSnapshot, getAssignStoreSnapshot)
  const myKids = store.children.filter((c) => !c.guest)
  // The SAME three-way partition as Profile (BOARDROOM-2026-09-02-152856
  // part C; parity per review F2): someone-else's (signing NOT farmed out)
  // vs DEFERRED (a linked guestparent seat record). LINKED deferred
  // ChildRecords keep their profile-flow affordance on Profile, so they keep
  // a pencil-carrying card here; assignment-DERIVED deferred entries (no
  // ChildRecord) are display-only there and get no card — nothing to edit.
  const guestKidsAll = store.children.filter((c) => c.guest)
  const guestKids = guestKidsAll.filter((c) => guestSigningState(c, store.assigned) !== 'parent')
  const deferredKids = guestKidsAll.filter((c) => guestSigningState(c, store.assigned) === 'parent')

  const selfCtx: GateContext = {
    genderPolicy: DEFAULT_WIZARD_CONFIG.genderPolicy,
    eventFields: DEFAULT_WIZARD_CONFIG.eventFields,
    subject: 'self',
    askLanguage: DEFAULT_WIZARD_CONFIG.askLanguage,
    askShirt: DEFAULT_WIZARD_CONFIG.askShirt,
    identityLocked,
  }
  const childCtx = useCallback((d: WizardDraft): GateContext => ({
    genderPolicy: DEFAULT_WIZARD_CONFIG.genderPolicy,
    eventFields: DEFAULT_WIZARD_CONFIG.eventFields,
    subject: 'child',
    suggestedShirt: suggestedJuniorShirt(ageFromDob(d)),
    askLanguage: DEFAULT_WIZARD_CONFIG.askLanguage,
    askShirt: DEFAULT_WIZARD_CONFIG.askShirt,
    identityLocked: false, // the lock is the ACCOUNT's, never a managed child's
  }), [])

  const editSelf = (row: SummaryRow) => navigate(`/get-ready/profile/step-${row.step}?hub=1`)
  const editChild = (key: string) => (row: SummaryRow) =>
    navigate(`/profile/children/${key}/step-${row.step}?hub=1`)
  const editLabel = (row: SummaryRow) => t('profile.hub.editRow', { field: row.k })

  const selfComplete = draft ? allValid(draft, selfCtx.genderPolicy, identityLocked) : false
  const selfName = draft ? `${draft.first} ${draft.last}`.trim() : ''

  const childCard = (c: ChildRecord) => {
    const d = childDraft(c)
    const note = childRowNote(c, t)
    return (
      <div key={c.key} data-testid="hub-child-card" data-child={c.key} style={{ marginBottom: 14 }}>
        <div style={cardHeadStyle}>
          <span style={{ fontSize: 14, fontWeight: 800 }}>
            {`${c.first} ${c.last}`.trim() || t('profile.child.fallbackName')}
          </span>
          {note && <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{note}</span>}
        </div>
        <SummaryCard
          rows={summaryRows(d, childCtx(d), t)}
          onEdit={editChild(c.key)}
          editLabel={editLabel}
        />
      </div>
    )
  }

  return (
    <div id="profilehub.root" style={{ maxWidth: 'var(--content-max)' }}>
      {/* Back to the Profile section — chevron swapped in code (README §RTL). */}
      <button
        data-testid="hub-back"
        onClick={() => navigate('/profile')}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 7, border: 0, background: 'transparent', color: 'var(--text-muted)', fontFamily: 'inherit', fontSize: 13, fontWeight: 700, cursor: 'pointer', minHeight: 44, padding: 0, marginBottom: 4 }}
      >{backChev} {t('profile.hub.back')}</button>

      {showPageHead && (
        <h1 data-testid="hub-head" style={{ fontSize: 24, fontWeight: 800, letterSpacing: '-0.02em', margin: '0 0 4px' }}>
          {t('profile.hub.title')}
        </h1>
      )}
      <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 16px' }}>{t('profile.hub.sub')}</p>

      {/* OWN card — honest loading / error / ready states, never blanks. */}
      <div id="profilehub.you">
        <div style={kickerStyle}>{t('profile.hub.kicker.you')}</div>
        {state === 'loading' ? (
          <p data-testid="hub-loading" role="status" className="secondary" style={{ fontSize: 13 }}>
            {t('profile.hub.loading')}
          </p>
        ) : state === 'error' || state === 'signedout' || !draft ? (
          <div data-testid="hub-load-failed" data-reason={state === 'signedout' ? 'signedout' : 'error'} role="alert" style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
            {/* F5: signed-out gets its own honest copy — never "check your
                connection" for a missing session. */}
            <p style={{ margin: '0 0 10px' }}>{t(state === 'signedout' ? 'profile.hub.signedOut' : 'profile.hub.loadFail')}</p>
            <button
              data-testid="hub-retry"
              onClick={() => setAttempt((a) => a + 1)}
              style={{ border: '1px solid rgba(63,224,200,.35)', borderRadius: 9, background: 'transparent', color: 'var(--accent-bright)', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', minHeight: 44, padding: '0 14px' }}
            >{t('profile.hub.retry')}</button>
          </div>
        ) : (
          <div data-testid="hub-own-card" style={{ marginBottom: 22 }}>
            {selfName && (
              <div style={cardHeadStyle}>
                <span style={{ fontSize: 14, fontWeight: 800 }}>{selfName}</span>
              </div>
            )}
            {/* Incomplete profile: the wizard receipt's own "Still needed" +
                "Add these now" voice — resume lands on the first incomplete
                step (identity-lock-aware), hub-scoped so Save returns here. */}
            {!selfComplete && (
              <div data-testid="hub-still-needed" style={warnBoxStyle}>
                <span style={{ flex: 'none', fontSize: 14, color: 'var(--warn)' }}>⚠</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11.5, lineHeight: 1.5, color: 'var(--warn)', marginBottom: 9 }}>
                    {t('wizard.done.still', { list: missingList(draft, selfCtx.genderPolicy, t, identityLocked) })}
                  </div>
                  <button
                    data-testid="hub-add-now"
                    onClick={() => navigate(`/get-ready/profile/step-${firstIncompleteStep(draft, selfCtx.genderPolicy, identityLocked)}?hub=1`)}
                    style={{ border: 0, borderRadius: 9, minHeight: 40, padding: '0 14px', background: 'var(--warn)', color: 'var(--warn-ink)', fontSize: 12.5, fontWeight: 800, fontFamily: 'inherit', cursor: 'pointer' }}
                  >{t('wizard.done.addNow')}</button>
                </div>
              </div>
            )}
            <SummaryCard
              rows={summaryRows(draft, selfCtx, t)}
              onEdit={editSelf}
              // L2 lock: no pencil on the read-only identity rows — no dead
              // affordance (the rows themselves still show the verified facts).
              editable={(r) => !(identityLocked && IDENTITY_ROW_IDS.has(r.id))}
              editLabel={editLabel}
            />
          </div>
        )}
      </div>

      {/* CHILD cards — the Profile groupings, same kickers, never merged. */}
      {myKids.length > 0 && (
        <div id="profilehub.children">
          <div style={kickerStyle}>{t('profile.managed.kicker')}</div>
          {myKids.map(childCard)}
        </div>
      )}
      {guestKids.length > 0 && (
        <div id="profilehub.guests">
          <div style={{ ...kickerStyle, margin: '6px 2px 8px' }}>{t('profile.managed.guestKicker')}</div>
          {guestKids.map(childCard)}
        </div>
      )}
      {/* DEFERRED group (review F2 — Profile-partition parity): LINKED
          ChildRecords whose signing went to their own parent keep their edit
          affordance (as on Profile), under Profile's own deferred kicker.
          Assignment-derived entries never render here (nothing to edit). */}
      {deferredKids.length > 0 && (
        <div id="profilehub.deferred">
          <div style={{ ...kickerStyle, margin: '6px 2px 8px' }}>{t('profile.managed.deferredKicker')}</div>
          {deferredKids.map(childCard)}
        </div>
      )}
    </div>
  )
}
